import { QueryClient } from "@tanstack/react-query";
import { client, UserDetailsDto } from "@/client";
import { findTableSettings } from "./table-settings-adapter";
import {
  tableSettingsToSearch,
  TableSettingsSearchOptions,
} from "./table-settings-layout";

/**
 * How a saved sort / page size reaches a table route's URL (ID-6897): as the route's
 * `validateSearch` default, read synchronously from the cached /me. The router validates a
 * destination's search before it loads -- for an in-app entry when the link is followed, for a
 * fresh URL when the app boots (`primeAccount` in src/state/prime-account.ts loads /me first) --
 * and writes the result into the URL, so the saved value is in place before the page renders:
 * one render, one list request, no redirect. Seeding from `beforeLoad` or from a component cannot
 * do this: by then the first validation has already written the route's own default into the
 * URL, and a navigation after mount renders and fetches twice.
 */

/** Every saved URL value for `tableKey` (the sort as the table's JSON entries, the page size), or {} when nothing applies: for a route with a sortField to default both. Sessions, with no sort, uses savedPageSize. */
export const savedTableSearch = (
  queryClient: QueryClient,
  tableKey: string,
  options: TableSettingsSearchOptions = {},
): Record<string, unknown> => {
  const me = queryClient.getQueryData<UserDetailsDto>(client.users.me.queryKey);
  return tableSettingsToSearch(
    findTableSettings(me?.userSettings, tableKey),
    options,
  );
};

/** The saved page size for `tableKey`: undefined until /me is cached, when nothing is saved, or when the saved size is not one the table offers. */
export const savedPageSize = (
  queryClient: QueryClient,
  tableKey: string,
  pageOptions?: readonly number[],
): number | undefined => {
  const { limit } = savedTableSearch(queryClient, tableKey, { pageOptions });
  return typeof limit === "number" ? limit : undefined;
};
